import axiosInstance from '../helpers/axios';
import { officeConstants } from './constants';
import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { MTLLoader } from 'three/examples/jsm/loaders/MTLLoader';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils';

const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY || 'your-32-character-secret-key1234567890';

const MODEL_DEBUG =
    typeof window !== 'undefined'
        ? window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        : process.env.NODE_ENV !== 'production';

const debugLog = (...args) => {
    if (!MODEL_DEBUG) return;
    console.log(...args);
};

const decryptData = async (modelPath, encryptedData) => {
    if (!encryptedData || !encryptedData.encrypted || !encryptedData.iv) {
        return null;
    }

    const { encrypted, iv } = encryptedData;

    try {
        const key = await crypto.subtle.importKey(
            'raw',
            new TextEncoder().encode(ENCRYPTION_KEY.padEnd(32, '\0').slice(0, 32)),
            { name: 'AES-CBC', length: 256 },
            false,
            ['decrypt']
        );

        const encryptedUint8 = new Uint8Array(atob(encrypted).split('').map((char) => char.charCodeAt(0)));
        const ivUint8 = new Uint8Array(atob(iv).split('').map((char) => char.charCodeAt(0)));

        return crypto.subtle.decrypt(
            { name: 'AES-CBC', iv: ivUint8 },
            key,
            encryptedUint8
        );
    } catch (error) {
        debugLog('[AdminModelDebug][Decrypt][FAIL]', modelPath, error);
        return null;
    }
};

const getGeometryDimensions = (geometry) => {
    geometry.computeBoundingBox();

    const box = geometry.boundingBox;
    const size = new THREE.Vector3();

    box.getSize(size);

    return {
        x: size.x.toFixed(2),
        y: size.y.toFixed(2),
        z: size.z.toFixed(2),
    };
};

const summarizeMaterial = (mat, index = 0) => {
    if (!mat) {
        return {
            index,
            ok: false,
            name: 'null',
        };
    }

    return {
        index,
        ok: true,
        type: mat.type,
        name: mat.name || `mat_${index}`,
        hasMap: !!mat.map,
        hasNormalMap: !!mat.normalMap,
        hasRoughnessMap: !!mat.roughnessMap,
        hasMetalnessMap: !!mat.metalnessMap,
        hasAoMap: !!mat.aoMap,
        hasEmissiveMap: !!mat.emissiveMap,
        color: mat.color ? `#${mat.color.getHexString()}` : null,
        roughness: typeof mat.roughness === 'number' ? mat.roughness : null,
        metalness: typeof mat.metalness === 'number' ? mat.metalness : null,
    };
};

const patchMtlTexturePaths = (mtlText, textureUrls = {}) => {
    return String(mtlText || '')
        .split(/\r?\n/)
        .map((line) => {
            const trimmed = line.trim();

            if (!trimmed) return line;

            const tokens = trimmed.split(/\s+/);
            const cmd = tokens[0]?.toLowerCase();

            const isTextureCmd =
                cmd === 'map_kd' ||
                cmd === 'map_ka' ||
                cmd === 'map_ks' ||
                cmd === 'map_ke' ||
                cmd === 'map_bump' ||
                cmd === 'bump' ||
                cmd === 'norm' ||
                cmd === 'disp' ||
                cmd === 'decal' ||
                cmd === 'refl' ||
                cmd === 'map_d' ||
                cmd === 'map_pr' ||
                cmd === 'map_pm' ||
                cmd === 'map_ns';

            if (!isTextureCmd) return line;

            const lastToken = tokens[tokens.length - 1];
            const normalizedName = lastToken.split('/').pop().split('\\').pop();

            if (textureUrls[lastToken]) {
                tokens[tokens.length - 1] = textureUrls[lastToken];
                return tokens.join(' ');
            }

            if (textureUrls[normalizedName]) {
                tokens[tokens.length - 1] = textureUrls[normalizedName];
                return tokens.join(' ');
            }

            return line;
        })
        .join('\n');
};

const buildGeometryFromGroup = (sourceGeometry, group) => {
    const geometry = new THREE.BufferGeometry();
    const attributes = sourceGeometry.attributes || {};
    const attributeNames = Object.keys(attributes);
    const hasIndex = !!sourceGeometry.index;

    if (hasIndex) {
        const indexArray = sourceGeometry.index.array.slice(group.start, group.start + group.count);

        attributeNames.forEach((attrName) => {
            const attr = sourceGeometry.getAttribute(attrName);

            if (!attr) return;

            const itemSize = attr.itemSize;
            const ArrayType = attr.array.constructor;
            const result = new ArrayType(indexArray.length * itemSize);

            for (let i = 0; i < indexArray.length; i += 1) {
                const srcIndex = indexArray[i];

                for (let j = 0; j < itemSize; j += 1) {
                    result[i * itemSize + j] = attr.array[srcIndex * itemSize + j];
                }
            }

            geometry.setAttribute(attrName, new THREE.BufferAttribute(result, itemSize, attr.normalized));
        });
    } else {
        attributeNames.forEach((attrName) => {
            const attr = sourceGeometry.getAttribute(attrName);

            if (!attr) return;

            const itemSize = attr.itemSize;
            const start = group.start * itemSize;
            const end = (group.start + group.count) * itemSize;
            const ArrayType = attr.array.constructor;
            const sliced = attr.array.slice(start, end);
            const result = new ArrayType(sliced);

            geometry.setAttribute(attrName, new THREE.BufferAttribute(result, itemSize, attr.normalized));
        });
    }

    if (!geometry.getAttribute('normal') && geometry.getAttribute('position')) {
        geometry.computeVertexNormals();
    }

    return geometry;
};

const collectMergedGeometryAndMaterials = (root, label = 'ADMIN_MODEL') => {
    const geometries = [];
    const materialsArray = [];
    const materialMap = new Map();

    root.traverse((child) => {
        if (!child?.isMesh || !child.geometry) return;

        child.updateWorldMatrix(true, false);

        const baseGeometry = child.geometry.clone();

        baseGeometry.applyMatrix4(child.matrixWorld);

        if (!baseGeometry.getAttribute('normal') && baseGeometry.getAttribute('position')) {
            baseGeometry.computeVertexNormals();
        }

        const localMaterials = Array.isArray(child.material)
            ? child.material
            : [child.material || new THREE.MeshStandardMaterial({ color: 0x808080 })];

        const groups = Array.isArray(baseGeometry.groups) && baseGeometry.groups.length
            ? [...baseGeometry.groups]
            : [
                {
                    start: 0,
                    count: baseGeometry.index
                        ? baseGeometry.index.count
                        : baseGeometry.getAttribute('position').count,
                    materialIndex: 0,
                },
            ];

        debugLog(`[AdminModelDebug][${label}][Mesh]`, {
            meshName: child.name || 'unnamed_mesh',
            materialCount: localMaterials.length,
            groupCount: groups.length,
            materials: localMaterials.map((mat, index) => summarizeMaterial(mat, index)),
        });

        groups.forEach((group, groupIndex) => {
            const localMat = localMaterials[group.materialIndex] || localMaterials[0] || new THREE.MeshStandardMaterial({ color: 0x808080 });
            const matKey = localMat.uuid || `${localMat.name || 'mat'}_${group.materialIndex}`;

            if (!materialMap.has(matKey)) {
                materialsArray.push(localMat);
                materialMap.set(matKey, materialsArray.length - 1);
            }

            const globalMaterialIndex = materialMap.get(matKey);
            const splitGeometry = buildGeometryFromGroup(baseGeometry, group);
            const splitCount = splitGeometry.index
                ? splitGeometry.index.count
                : splitGeometry.getAttribute('position').count;

            splitGeometry.clearGroups();
            splitGeometry.addGroup(0, splitCount, globalMaterialIndex);
            geometries.push(splitGeometry);

            debugLog(`[AdminModelDebug][${label}][GroupSplit]`, {
                meshName: child.name || 'unnamed_mesh',
                groupIndex,
                localMaterialIndex: group.materialIndex,
                globalMaterialIndex,
                splitCount,
                material: summarizeMaterial(localMat, globalMaterialIndex),
            });
        });
    });

    if (!geometries.length) {
        throw new Error('Не найдено ни одной mesh-геометрии в модели');
    }

    const merged = mergeGeometries(geometries, true);

    if (!merged) {
        throw new Error('Не удалось объединить геометрию модели');
    }

    if (!merged.getAttribute('normal') && merged.getAttribute('position')) {
        merged.computeVertexNormals();
    }

    debugLog(`[AdminModelDebug][${label}][MergedResult]`, {
        geometriesCount: geometries.length,
        mergedGroups: merged.groups?.length || 0,
        materialsCount: materialsArray.length,
        materials: materialsArray.map((mat, index) => summarizeMaterial(mat, index)),
    });

    return {
        geometry: merged,
        materialsArray,
    };
};

const requestEncryptedModel = async (productId, onDownloadProgress) => {
    return axiosInstance.get(`/admin/product/model/${productId}`, {
        onDownloadProgress,
    });
};

export const getAdminProductModelData = (product, options = {}) => {
    return async (dispatch) => {
        let objUrl;
        let mtlUrl;
        let textureUrls = {};

        const productId = typeof product === 'string' ? product : product?._id;
        const fullName = typeof product === 'string' ? '' : product?.fullName || product?.name || '';

        if (!productId) {
            throw new Error('Не передан id товара для загрузки 3D-модели');
        }

        try {
            dispatch({
                type: officeConstants.GET_PRODUCT_MODEL_DATA_PROGRESS,
                payload: { productId, loaded: 0, total: 1, fullName },
            });

            dispatch({ type: officeConstants.GET_PRODUCT_MODEL_DATA_REQUEST });

            const response = await requestEncryptedModel(productId, (event) => {
                const loaded = Number(event?.loaded) || 0;
                const total = Number(event?.total) || loaded || 1;

                dispatch({
                    type: officeConstants.GET_PRODUCT_MODEL_DATA_PROGRESS,
                    payload: { productId, loaded, total, fullName },
                });
            });

            if (!response || !response.data) {
                throw new Error(`Сервер вернул пустой ответ для ${productId}`);
            }

            const encryptedData = response.data.encryptedData;

            debugLog('[AdminModelDebug][Loader][Response]', {
                productId,
                fileType: encryptedData?.fileType,
                hasEncryptedData: !!encryptedData,
                hasMtl: !!encryptedData?.mtl,
                textureKeys: Object.keys(encryptedData?.textures || {}),
            });

            if (!encryptedData) {
                throw new Error('Сервер не вернул зашифрованную модель');
            }

            const buffer = await decryptData(productId, encryptedData);

            if (!buffer) {
                throw new Error(`Не удалось расшифровать модель ${productId}`);
            }

            const fileType = (encryptedData.fileType || 'stl').toLowerCase();
            let geometry = null;
            let dimensions = null;
            let materials = null;

            debugLog('[AdminModelDebug][Loader][Decrypted]', {
                productId,
                fileType,
                byteLength: buffer?.byteLength || 0,
            });

            if (fileType === 'stl') {
                const loader = new STLLoader();

                geometry = loader.parse(buffer);
                geometry.computeVertexNormals();
                dimensions = getGeometryDimensions(geometry);

                debugLog('[AdminModelDebug][Loader][STL]', {
                    productId,
                    dimensions,
                });
            } else if (fileType === 'obj') {
                const mtlData = encryptedData.mtl
                    ? await decryptData(`${productId}.mtl`, encryptedData.mtl)
                    : null;

                const textures = encryptedData.textures || {};

                objUrl = URL.createObjectURL(new Blob([buffer], { type: 'text/plain' }));

                for (const [name, textureData] of Object.entries(textures)) {
                    const textureBuffer = await decryptData(`${productId}-${name}`, textureData);

                    if (textureBuffer) {
                        const ext = name.split('.').pop()?.toLowerCase() || 'png';
                        const mime =
                            ext === 'jpg' || ext === 'jpeg'
                                ? 'image/jpeg'
                                : ext === 'webp'
                                    ? 'image/webp'
                                    : 'image/png';

                        textureUrls[name] = URL.createObjectURL(new Blob([textureBuffer], { type: mime }));
                    }
                }

                debugLog('[AdminModelDebug][Loader][OBJ][TexturesPrepared]', {
                    productId,
                    textureKeysFromServer: Object.keys(textures),
                    textureBlobKeys: Object.keys(textureUrls),
                });

                const mtlLoader = new MTLLoader();
                const objLoader = new OBJLoader();

                if (mtlData) {
                    let mtlText = new TextDecoder().decode(mtlData);
                    const beforePatch = mtlText;

                    mtlText = patchMtlTexturePaths(mtlText, textureUrls);
                    mtlUrl = URL.createObjectURL(new Blob([mtlText], { type: 'text/plain' }));

                    debugLog('[AdminModelDebug][Loader][OBJ][MTL Patch]', {
                        productId,
                        changed: beforePatch !== mtlText,
                        sampleBefore: beforePatch.slice(0, 500),
                        sampleAfter: mtlText.slice(0, 500),
                    });

                    const parsedMaterials = mtlLoader.parse(mtlText, '');

                    parsedMaterials.preload();

                    debugLog('[AdminModelDebug][Loader][OBJ][MaterialsParsed]', {
                        productId,
                        materialNames: Object.keys(parsedMaterials?.materials || {}),
                        materials: Object.values(parsedMaterials?.materials || {}).map((mat, index) => summarizeMaterial(mat, index)),
                    });

                    objLoader.setMaterials(parsedMaterials);
                }

                const object = await new Promise((resolve, reject) => {
                    objLoader.load(objUrl, resolve, undefined, reject);
                });

                const mergedData = collectMergedGeometryAndMaterials(object, 'OBJ');

                geometry = mergedData.geometry;
                materials = { materialsArray: mergedData.materialsArray };
                dimensions = getGeometryDimensions(geometry);

                debugLog('[AdminModelDebug][Loader][OBJ][Final]', {
                    productId,
                    dimensions,
                    materialsCount: materials.materialsArray.length,
                    materials: materials.materialsArray.map((mat, index) => summarizeMaterial(mat, index)),
                });
            } else if (fileType === 'glb' || fileType === 'gltf') {
                const gltfLoader = new GLTFLoader();
                const dracoLoader = new DRACOLoader();

                dracoLoader.setDecoderPath('/draco/');
                gltfLoader.setDRACOLoader(dracoLoader);

                const gltfSource =
                    fileType === 'gltf'
                        ? new TextDecoder().decode(buffer)
                        : buffer;

                const gltf = await new Promise((resolve, reject) => {
                    gltfLoader.parse(gltfSource, '', resolve, reject);
                });

                dracoLoader.dispose();

                const rawSceneInfo = [];

                gltf.scene.traverse((child) => {
                    if (!child?.isMesh) return;

                    const mats = Array.isArray(child.material) ? child.material : [child.material];

                    rawSceneInfo.push({
                        meshName: child.name || 'unnamed_mesh',
                        materialCount: mats.length,
                        groupCount: child.geometry?.groups?.length || 0,
                        materials: mats.map((mat, index) => summarizeMaterial(mat, index)),
                    });
                });

                debugLog('[AdminModelDebug][Loader][GLTF][BeforeMerge]', {
                    productId,
                    sceneChildren: rawSceneInfo,
                });

                const mergedData = collectMergedGeometryAndMaterials(gltf.scene, 'GLTF');

                geometry = mergedData.geometry;
                materials = { materialsArray: mergedData.materialsArray };
                dimensions = getGeometryDimensions(geometry);

                debugLog('[AdminModelDebug][Loader][GLTF][Final]', {
                    productId,
                    dimensions,
                    mergedGroupCount: geometry.groups?.length || 0,
                    materialsCount: materials.materialsArray.length,
                    materials: materials.materialsArray.map((mat, index) => summarizeMaterial(mat, index)),
                });
            } else {
                throw new Error(`Неподдерживаемый тип модели: ${fileType}`);
            }

            const action = {
                type: officeConstants.GET_PRODUCT_MODEL_DATA_SUCCESS,
                payload: {
                    productId,
                    geometry,
                    materials: materials ? materials.materialsArray : null,
                    dimensions,
                    textures: encryptedData.textures || [],
                    fileType,
                    // Blob URL текстур живёт до явной очистки модели.
                    // Batch-анализ освобождает его после каждой формы.
                    resourceUrls: Object.values(textureUrls || {}),
                },
            };

            debugLog('[AdminModelDebug][Loader][DispatchSuccess]', {
                productId,
                fileType,
                dimensions,
                materialsCount: action.payload.materials?.length || 0,
                geometryGroupCount: action.payload.geometry?.groups?.length || 0,
            });

            if (options.cache !== false) {
                dispatch(action);
            }

            return action;
        } catch (error) {
            debugLog('[AdminModelDebug][Loader][FAIL]', {
                productId,
                message: error?.message,
                stack: error?.stack,
            });

            dispatch({
                type: officeConstants.GET_PRODUCT_MODEL_DATA_FAILURE,
                payload: { error: error.message || 'Ошибка загрузки модели' },
            });

            throw error;
        } finally {
            dispatch({
                type: officeConstants.CLEAR_PRODUCT_MODEL_MODEL_PROGRESS,
                payload: { productId },
            });

            if (objUrl) URL.revokeObjectURL(objUrl);
            if (mtlUrl) URL.revokeObjectURL(mtlUrl);

            /**
             * ВАЖНО:
             * textureUrls НЕ удаляем.
             * Эти blob-url остаются внутри материалов Three.js.
             * Если удалить их здесь, текстуры могут стать чёрными или не успеть загрузиться.
             */
        }
    };
};

export const checkOfficeAccess = () => {
    return async (dispatch, getState) => {
        try {
            dispatch({ type: officeConstants.CHECK_OFFICE_ACCESS_REQUEST });

            const { auth: { user, authenticate } } = getState();

            if (!authenticate || !['partner', 'storeOwner', 'officeWorker', 'admin', 'superAdmin'].includes(user.role)) {
                dispatch({
                    type: officeConstants.CHECK_OFFICE_ACCESS_SUCCESS,
                    payload: { hasAccess: false, office: null },
                });
                return;
            }

            const response = await axiosInstance.get('/office/get');
            const office = response.data.offices.find((item) =>
                item.allowedUsers.some((userItem) => userItem._id === user._id)
            );

            dispatch({
                type: officeConstants.CHECK_OFFICE_ACCESS_SUCCESS,
                payload: {
                    hasAccess: !!office,
                    office: office || null,
                },
            });
        } catch (error) {
            dispatch({
                type: officeConstants.CHECK_OFFICE_ACCESS_FAILURE,
                payload: { error: error.response?.data?.message || 'Ошибка проверки доступа' },
            });
        }
    };
};

export const getOffices = () => {
    return async (dispatch) => {
        try {
            dispatch({ type: officeConstants.GET_OFFICES_REQUEST });

            const response = await axiosInstance.get('/office/get');

            dispatch({
                type: officeConstants.GET_OFFICES_SUCCESS,
                payload: { offices: response.data.offices },
            });
        } catch (error) {
            dispatch({
                type: officeConstants.GET_OFFICES_FAILURE,
                payload: { error: error.response?.data?.message || 'Ошибка загрузки офисов' },
            });
        }
    };
};

export const updateOfficeUsers = (officeId, userIds, action) => {
    return async (dispatch) => {
        try {
            dispatch({ type: officeConstants.UPDATE_OFFICE_USERS_REQUEST });

            const response = await axiosInstance.post('/office/updateUsers', { officeId, userIds, action });

            dispatch({
                type: officeConstants.UPDATE_OFFICE_USERS_SUCCESS,
                payload: { office: response.data.office },
            });
        } catch (error) {
            dispatch({
                type: officeConstants.UPDATE_OFFICE_USERS_FAILURE,
                payload: { error: error.response?.data?.message || 'Ошибка обновления пользователей' },
            });
        }
    };
};


const buildOfficeRequestData = (officeData = {}) => {
    const formData = new FormData();

    const appendText = (key, value) => {
        if (value === undefined || value === null) return;
        formData.append(key, String(value));
    };

    appendText("name", officeData.name);
    appendText("address", officeData.address);
    appendText("accentColor", officeData.accentColor);

    if (Array.isArray(officeData.categories)) {
        formData.append("categories", JSON.stringify(officeData.categories));
    }

    if (Array.isArray(officeData.allowedUsers)) {
        formData.append("allowedUsers", JSON.stringify(officeData.allowedUsers));
    }

    if (typeof File !== "undefined" && officeData.coverImageFile instanceof File) {
        formData.append("coverImage", officeData.coverImageFile);
    }

    if (typeof File !== "undefined" && officeData.watermarkImageFile instanceof File) {
        formData.append("watermarkImage", officeData.watermarkImageFile);
    }

    if (officeData.removeCoverImage === true) {
        formData.append("removeCoverImage", "true");
    }

    if (officeData.removeWatermarkImage === true) {
        formData.append("removeWatermarkImage", "true");
    }

    return formData;
};

export const createOffice = (officeData) => {
    return async (dispatch) => {
        try {
            dispatch({ type: officeConstants.CREATE_OFFICE_REQUEST });

            const response = await axiosInstance.post(
                '/office/create',
                buildOfficeRequestData(officeData)
            );

            dispatch({
                type: officeConstants.CREATE_OFFICE_SUCCESS,
                payload: { office: response.data.office },
            });

            return response.data;
        } catch (error) {
            dispatch({
                type: officeConstants.CREATE_OFFICE_FAILURE,
                payload: { error: error.response?.data?.message || 'Ошибка создания офиса' },
            });

            throw error;
        }
    };
};

export const updateOffice = (officeId, officeData) => {
    return async (dispatch) => {
        try {
            dispatch({ type: officeConstants.UPDATE_OFFICE_REQUEST });

            const response = await axiosInstance.put(
                `/office/update/${officeId}`,
                buildOfficeRequestData(officeData)
            );

            dispatch({
                type: officeConstants.UPDATE_OFFICE_SUCCESS,
                payload: { office: response.data.office },
            });

            return response.data;
        } catch (error) {
            dispatch({
                type: officeConstants.UPDATE_OFFICE_FAILURE,
                payload: { error: error.response?.data?.message || 'Ошибка обновления офиса' },
            });

            throw error;
        }
    };
};

export const getOfficeProducts = (officeId) => {
    return async (dispatch) => {
        try {
            dispatch({ type: officeConstants.GET_OFFICE_PRODUCTS_REQUEST });

            const response = await axiosInstance.get(`/office/products/${officeId}`);

            dispatch({
                type: officeConstants.GET_OFFICE_PRODUCTS_SUCCESS,
                payload: { products: response.data.products },
            });
        } catch (error) {
            dispatch({
                type: officeConstants.GET_OFFICE_PRODUCTS_FAILURE,
                payload: { error: error.response?.data?.message || 'Ошибка загрузки продуктов' },
            });
        }
    };
};
